import { Navigate, Outlet, createRootRouteWithContext, useLocation } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { MetaTitle } from '../components/meta-title';
import { Toast, type ToastItem } from '../components/toast';
import { addTrackCrumb, trackError } from '../error/tracking';
import { captureException } from '../logger/capture-exception';
import { alignHelpScoutIcon } from '../services/helpscout';
import { ToastContext } from '../shared/contexts/toast-context';

export type RouterContext = {
  auth?: {
    userId?: string | null;
  };
};

export const Route = createRootRouteWithContext<RouterContext>()({
  // eslint-disable-next-line no-use-before-define
  component: Root,
  notFoundComponent: () => <Navigate to="/app" />,
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '__root',
      level: 'info',
    });
  },
});

function Root() {
  const { pathname } = useLocation();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [isToastOpen, setIsToastOpen] = useState(false);
  function addToast(toast: ToastItem) {
    setToasts([...toasts, toast]);
    setIsToastOpen(true);
  }

  function toastCloseHandler(id: number) {
    const clearedToasts = toasts.filter(function (toast) {
      return toast.id !== id;
    });
    setIsToastOpen(clearedToasts.length > 0);
    setToasts(clearedToasts);
  }

  const value = { toasts, addToast };

  useEffect(
    function () {
      alignHelpScoutIcon(pathname).catch(function (err) {
        captureException(
          err,
          {
            file: 'router.tsx',
            method: 'alignHelpScoutIcon',
            infoMessage: 'unable to align HelpScout Beacon',
          },
          trackError
        );
      });
    },
    [pathname]
  );

  return (
    <>
      <MetaTitle />
      <ToastContext.Provider value={value}>
        <Outlet />
        <Toast
          open={isToastOpen}
          onOpenChange={setIsToastOpen}
          toasts={toasts}
          onToastClose={toastCloseHandler}
        />
      </ToastContext.Provider>
    </>
  );
}
